import { useEffect } from 'react';
import {
  createFileRoute,
  redirect,
  useRouter,
  useNavigate,
} from '@tanstack/react-router';
import { Heading, Text } from 'react-aria-components';
import { z } from 'zod';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ApolloError, useMutation } from '@apollo/client';
import Button from 'src/components/ui/Button';
import LinkButton from 'src/components/ui/LinkButton';
import PageLoader from 'src/components/ui/PageLoader';
import Logo from 'src/components/ui/Logo';
import { useAuth } from 'src/utils/auth';
import { graphQLErrorNotification } from 'src/utils/notifications';
import VERIFY_LOGIN_MUTATION from 'src/graphql/mutation/verify-login.graphql';

import type { ClientToken } from 'src/types/client';

const VerifyLoginPage = () => {
  const { email, code } = Route.useSearch();
  const auth = useAuth();
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [verifyLogin, { loading, error, called }] = useMutation(
    VERIFY_LOGIN_MUTATION,
    {
      onCompleted: async (data) => {
        auth.onLogin(data.verifyLogin as ClientToken);
        await router.invalidate();
      },
      onError: graphQLErrorNotification,
    }
  );

  useEffect(() => {
    document.body.classList.add('login');

    const abortController = new AbortController();

    const callback = async () => {
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }

      const token = await executeRecaptcha('verifyLogin');

      await verifyLogin({
        variables: { input: { email, code, recaptcha: token } },
        context: {
          fetchOptions: { signal: abortController.signal },
        },
      });
    };

    // Apollo error link handles errors; suppress rejection to avoid Sentry duplicate via onunhandledrejection
    callback().catch(() => {});

    return () => {
      document.body.classList.remove('login');
      abortController.abort();
    };
  }, [code, email, executeRecaptcha, verifyLogin]);

  if (!called || loading) return <PageLoader />;

  return (
    <div className="w-full max-w-[26.75rem] mx-auto bg-surface-alt rounded-3xl shadow-3 text-center p-11">
      <Logo className="mb-11" />
      {error && <ErrorContent email={email} error={error} />}
    </div>
  );
};

const ErrorContent = ({
  email = '',
  error,
}: {
  email?: string;
  error?: ApolloError;
}) => {
  const navigate = useNavigate();
  const errors = error?.graphQLErrors[0]?.extensions?.fields ?? {};
  const errorText = Object.values(errors).join(', ');

  return (
    <>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Something went wrong!
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-4 mb-8"
      >
        {errorText}
      </Text>
      <Button
        onPress={() =>
          navigate({ to: '/otp-login', search: { email }, replace: true })
        }
      >
        Use One-time Password
      </Button>
      <Text
        elementType="span"
        className="block text-sm text-surface-alt-75 font-normal my-1"
      >
        or
      </Text>
      <LinkButton
        reversed
        onPress={() => navigate({ to: '/login', replace: true })}
      >
        Back to Sign In
      </LinkButton>
    </>
  );
};

const VerifySchema = z.object({
  email: z.string().email(),
  code: z.string().length(64),
  courseId: z.number().optional(),
  moduleId: z.number().optional(),
});

export const Route = createFileRoute('/_auth/verify-login')({
  validateSearch: (search) => VerifySchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { success } = VerifySchema.safeParse(search);

    if (!success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/login',
        replace: true,
      });
    }
  },
  component: VerifyLoginPage,
});
